'use client';

import { useMemo, useState } from 'react';
import CurrencySelector from '@/components/utils/CurrencySelector';
import CalculatorInputs from './CalculatorInputs';
import ResultsDisplay from './ResultsDisplay';
import { calculateEvEbitda } from './calculations';
import { EvEbitdaValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  translations: ToolsTranslations['evEbitda'];
}

export default function EvEbitdaCalculator({ translations }: Props) {
  const [values, setValues] = useState<EvEbitdaValues>({
    enterpriseValue: 5000000000, // 5 billion
    ebitda: 600000000, // 600 million
    currency: 'kr',
  });

  const handleValuesChange = (field: keyof EvEbitdaValues, value: number | string) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  const result = useMemo(() => calculateEvEbitda(values, translations), [values, translations]);

  return (
    <section id="ev-ebitda-calculator" className="w-full my-12 tools">
      <div className="rounded-xl border border-gray-200 shadow-sm bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-blue-900">{translations.title}</h2>
              <p className="text-sm text-gray-600 mt-1">{translations.subtitle}</p>
            </div>
            <div className="w-56">
              <CurrencySelector
                value={values.currency}
                onChange={(currency) => handleValuesChange('currency', currency)}
                showArrow={false}
                localStorageKey="currencyPreference"
              />
            </div>
          </div>
        </div>
        <div className="p-6 space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <CalculatorInputs values={values} onValuesChange={handleValuesChange} translations={translations} />
            <ResultsDisplay
              result={result}
              translations={translations}
              enterpriseValue={values.enterpriseValue}
              ebitda={values.ebitda}
              currency={values.currency}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
